import React, { useState, useEffect, useCallback } from 'react';
import activityLogService from '../services/activityLogService';
import LogDetailViewer from '../components/history/LogDetailViewer';
import DateRangeFilter from '../components/common/DateRangeFilter';
import { formatInStoreTimezone } from '../utils/dateUtils';
import { getActivityActionLabel, getActivitySummary } from '../utils/activityLogFormatting';
import useStoreTimezone from '../hooks/useStoreTimezone';

const ActivityLogPage = () => {
  const timezone = useStoreTimezone();
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filterOptions, setFilterOptions] = useState({ actions: [], users: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [filters, setFilters] = useState({ period: 'today', action: '', userId: '' });

  const fetchLogs = useCallback(async (page = 1, currentFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const res = await activityLogService.getLogs(page, currentFilters);
      setLogs(res.data);
      setPagination(res.pagination);
      setFilterOptions(res.filters || { actions: [], users: [] });
    } catch (fetchError) {
      setError(fetchError?.response?.data?.message || 'Failed to fetch activity logs.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchLogs(1, filters);
  }, [filters, fetchLogs]);

  const toggleDetails = (logId) => {
    setExpandedLogId(expandedLogId === logId ? null : logId);
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex flex-col gap-4 mb-6 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Activity History</h1>
          <p className="text-sm text-gray-500 mt-1">Filter by date, action type, or operator to narrow the audit trail.</p>
        </div>
        <div className="flex flex-col gap-3 xl:items-end">
          <DateRangeFilter
            selectedPeriod={filters.period}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onSelectPreset={(period, range) => {
              setFilters((prev) => ({
                ...prev,
                period,
                startDate: range.start,
                endDate: range.end,
              }));
            }}
            onApplyCustomRange={(startDate, endDate) => {
              setFilters((prev) => ({
                ...prev,
                period: 'custom',
                startDate,
                endDate,
              }));
            }}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <div>
              <label htmlFor="activity-action-filter" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                Type of Action
              </label>
              <select
                id="activity-action-filter"
                value={filters.action}
                onChange={(event) => {
                  const action = event.target.value;
                  setFilters((prev) => ({ ...prev, action }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white min-w-[220px]"
              >
                <option value="">All actions</option>
                {filterOptions.actions.map((action) => (
                  <option key={action} value={action}>
                    {getActivityActionLabel(action)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="activity-user-filter" className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                User
              </label>
              <select
                id="activity-user-filter"
                value={filters.userId}
                onChange={(event) => {
                  const userId = event.target.value;
                  setFilters((prev) => ({ ...prev, userId }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white min-w-[220px]"
              >
                <option value="">All users</option>
                {filterOptions.users.map((user) => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date & Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                  No activity logs match the selected filters.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                  Loading activity history…
                </td>
              </tr>
            )}
            {!loading && logs.map(log => (
              <React.Fragment key={log._id}>
                <tr onClick={() => toggleDetails(log._id)} className="cursor-pointer hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatInStoreTimezone(log.createdAt, 'dd-MM-yyyy hh:mm a', timezone)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">{log.user?.name || 'System'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="font-medium text-gray-900">{getActivityActionLabel(log.action)}</div>
                    <div className="font-mono text-xs text-gray-500">{log.action}</div>
                  </td>
                  <td className="px-6 py-4 text-sm">{getActivitySummary(log)}</td>
                </tr>
                {expandedLogId === log._id && (
                  <tr>
                    <td colSpan="4" className="p-0">
                      <LogDetailViewer log={log} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-between items-center">
        <button onClick={() => fetchLogs(pagination.page - 1, filters)} disabled={loading || !pagination.page || pagination.page === 1} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Previous</button>
        <span>Page {pagination.page || 1} of {pagination.pages || 1}</span>
        <button onClick={() => fetchLogs(pagination.page + 1, filters)} disabled={loading || !pagination.page || pagination.page === pagination.pages} className="px-4 py-2 bg-gray-300 rounded disabled:opacity-50">Next</button>
      </div>
    </div>
  );
};

export default ActivityLogPage;
